import { useState, useMemo, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailAuth } from "../lib/auth-context";
import { useCart } from "../lib/cart-context";
import { Search, X, ShoppingCart, ChevronRight, Package, Loader2, Zap, Minus, Plus, List, LayoutGrid, ChevronUp, ChevronDown } from "lucide-react";

interface CatalogueItem {
  id: string;
  sku: string | null;
  nomFr: string;
  descriptionFr: string | null;
  type: string;
  imageUrl: string | null;
  robotsCompatibles: string[];
  poidsNet: number | null;
  longueur: number | null;
  largeur: number | null;
  financement24m: number | null;
  financement36m: number | null;
  financement48m: number | null;
  financementMin: number | null;
  maintenanceMensuelle: number | null;
  forfaitDeploiement: number | null;
  prixAffiche: number | null;
  statutLabel: string;
  locationDisponible: boolean | null;
  locationDelaiInstallation: string | null;
  locationJourPartenaire: number | null;
  locationSemainePartenaire: number | null;
  locationMoisPartenaire: number | null;
  // Champs enrichis robots
  features: string[] | null;
  specifications: string[] | null;
  categorieRobot: string | null;
  loc24Mois: number | null;
  loc36Mois: number | null;
  loc48Mois: number | null;
  statutProduit: string;
}

interface ExtensionGarantie {
  id: string;
  codeArticle: string;
  robotNom: string;
  dureeAns: number;
  prixRevendeur: string | null;
}

function useCatalogue() {
  const [data, setData] = useState<{ items: CatalogueItem[]; statutLabel: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    authFetch("/api/portail/catalogue?type=machine")
      .then(r => r.ok ? r.json() : Promise.reject(new Error("Erreur chargement catalogue")))
      .then(json => { if (!cancelled) setData(json); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { items: data?.items ?? [], statutLabel: data?.statutLabel ?? "", isLoading, error };
}

function formatPrix(p: number | null) {
  if (p === null) return "Prix sur demande";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p);
}

function QtySelector({ qty, onChange }: { qty: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, qty - 1))}
        className="w-7 h-7 flex items-center justify-center rounded-md border border-border text-foreground hover:bg-secondary/60 transition-colors"
        data-testid="button-qty-minus"
      >
        <Minus className="w-3 h-3" />
      </button>
      <input
        type="number"
        min={1}
        value={qty}
        onChange={e => onChange(Math.max(1, parseInt(e.target.value) || 1))}
        className="w-10 text-center text-sm font-medium bg-background border border-border rounded-md py-1 focus:outline-none focus:ring-2 focus:ring-primary/30"
        data-testid="input-qty"
      />
      <button
        type="button"
        onClick={() => onChange(qty + 1)}
        className="w-7 h-7 flex items-center justify-center rounded-md border border-border text-foreground hover:bg-secondary/60 transition-colors"
        data-testid="button-qty-plus"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function DetailPanel({ item, onClose, statutLabel, primary }: {
  item: CatalogueItem;
  onClose: () => void;
  statutLabel: string;
  primary: string;
}) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [extensions, setExtensions] = useState<ExtensionGarantie[]>([]);

  useEffect(() => {
    if (item.type !== "machine") return;
    authFetch(`/api/portail/extensions/${item.id}`)
      .then(r => r.json())
      .then(data => setExtensions(Array.isArray(data) ? data : []))
      .catch(() => setExtensions([]));
  }, [item.id]);

  const financements = [
    item.financement24m ? { mois: 24, val: item.financement24m } : null,
    item.financement36m ? { mois: 36, val: item.financement36m } : null,
    item.financement48m ? { mois: 48, val: item.financement48m } : null,
  ].filter(Boolean) as { mois: number; val: number }[];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-xl h-full bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-lg font-semibold text-foreground">{item.nomFr}</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary/60 text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 p-6 space-y-6">
          <div className="rounded-xl overflow-hidden bg-white dark:bg-zinc-900 border border-border" style={{ height: 200 }}>
            {item.imageUrl ? (
              <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-16 h-16 text-muted-foreground/30" />
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
            <h3 className="text-xl font-bold text-foreground mt-1">{item.nomFr}</h3>
            {item.descriptionFr && (
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.descriptionFr}</p>
            )}
          </div>

          <div className="bg-muted/50 rounded-xl p-4 border border-border">
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-foreground">{formatPrix(item.prixAffiche)}</span>
              {item.prixAffiche !== null && <span className="text-sm text-muted-foreground mb-1">HT</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Tarif {statutLabel}</p>

            {financements.length > 0 && (
              <div className="mt-4 border-t border-border pt-3">
                <p className="text-xs font-medium text-foreground mb-2">Options de financement</p>
                <div className="grid grid-cols-3 gap-2">
                  {financements.map(f => (
                    <div key={f.mois} className="bg-background rounded-lg p-2 text-center border border-border">
                      <p className="text-xs text-muted-foreground">{f.mois} mois</p>
                      <p className="text-sm font-semibold text-foreground">{formatPrix(f.val)}<span className="text-xs font-normal">/m</span></p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Caractéristiques & Spécifications — robots uniquement */}
          {item.type === 'robot' && (item.features?.length || item.specifications?.length) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {item.features && item.features.length > 0 && (
                <div className="bg-muted/30 rounded-xl p-4 border border-border">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Caractéristiques</p>
                  <ul className="space-y-1">
                    {item.features.map((f, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <span className="text-primary mt-0.5 shrink-0">•</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.specifications && item.specifications.length > 0 && (
                <div className="bg-muted/30 rounded-xl p-4 border border-border">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Spécifications</p>
                  <ul className="space-y-1">
                    {item.specifications.map((s, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <span className="text-primary mt-0.5 shrink-0">•</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Grille tarifaire location longue durée — robots */}
          {item.type === 'robot' && (item.loc24Mois || item.loc36Mois || item.loc48Mois || item.maintenanceMensuelle || item.forfaitDeploiement) && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Tarification mensuelle</p>
              <div className="overflow-hidden rounded-xl border border-border text-sm">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="text-left p-2.5 font-medium text-muted-foreground text-xs">Formule</th>
                      <th className="text-right p-2.5 font-medium text-muted-foreground text-xs">Mensualité HT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {item.loc24Mois && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Location 24 mois</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.loc24Mois)}<span className="text-xs font-normal text-muted-foreground">/m</span></td>
                      </tr>
                    )}
                    {item.loc36Mois && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Location 36 mois</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.loc36Mois)}<span className="text-xs font-normal text-muted-foreground">/m</span></td>
                      </tr>
                    )}
                    {item.loc48Mois && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Location 48 mois</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.loc48Mois)}<span className="text-xs font-normal text-muted-foreground">/m</span></td>
                      </tr>
                    )}
                    {item.maintenanceMensuelle && (
                      <tr className="border-t-2 border-border">
                        <td className="p-2.5 text-muted-foreground">Maintenance mensuelle conseillée</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.maintenanceMensuelle)}<span className="text-xs font-normal text-muted-foreground">/m</span></td>
                      </tr>
                    )}
                    {item.forfaitDeploiement && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Forfait déploiement</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.forfaitDeploiement)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Spécifications techniques — accessoires/machines */}
          {item.type !== 'robot' && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-3">Spécifications techniques</p>
              <div className="space-y-1.5">
                {item.poidsNet && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Poids net</span>
                    <span className="text-foreground font-medium">{item.poidsNet} kg</span>
                  </div>
                )}
                {item.longueur && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Longueur</span>
                    <span className="text-foreground font-medium">{item.longueur} mm</span>
                  </div>
                )}
                {item.largeur && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Largeur</span>
                    <span className="text-foreground font-medium">{item.largeur} mm</span>
                  </div>
                )}
                {item.maintenanceMensuelle && (
                  <div className="flex justify-between text-sm border-t border-border pt-1.5 mt-1.5">
                    <span className="text-muted-foreground">Maintenance mensuelle conseillée</span>
                    <span className="text-foreground font-medium">{formatPrix(item.maintenanceMensuelle)}/m</span>
                  </div>
                )}
                {item.forfaitDeploiement && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Forfait déploiement</span>
                    <span className="text-foreground font-medium">{formatPrix(item.forfaitDeploiement)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {item.robotsCompatibles?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Robots compatibles</p>
              <div className="flex flex-wrap gap-1.5">
                {item.robotsCompatibles.map(r => (
                  <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{r}</span>
                ))}
              </div>
            </div>
          )}

          {item.type === "machine" && item.locationJourPartenaire && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-foreground">Tarifs de location</p>
                {item.locationDisponible ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">✅ Disponible</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Sur demande</span>
                )}
              </div>
              <div className="overflow-hidden rounded-xl border border-border text-sm">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="text-left p-2.5 font-medium text-muted-foreground text-xs">Période</th>
                      <th className="text-right p-2.5 font-medium text-muted-foreground text-xs">Tarif HT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {item.locationJourPartenaire && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Journalière</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.locationJourPartenaire)}</td>
                      </tr>
                    )}
                    {item.locationSemainePartenaire && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Hebdomadaire</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.locationSemainePartenaire)}</td>
                      </tr>
                    )}
                    {item.locationMoisPartenaire && (
                      <tr>
                        <td className="p-2.5 text-muted-foreground">Mensuelle</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{formatPrix(item.locationMoisPartenaire)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {item.locationDelaiInstallation && (
                <p className="text-xs text-muted-foreground mt-1.5">Délai d'installation : {item.locationDelaiInstallation}</p>
              )}
            </div>
          )}

          {extensions.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Extensions de garantie</p>
              <div className="overflow-hidden rounded-xl border border-border text-sm">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="text-left p-2.5 font-medium text-muted-foreground text-xs">Extension</th>
                      <th className="text-right p-2.5 font-medium text-muted-foreground text-xs">Prix HT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {extensions.map(ext => (
                      <tr key={ext.id}>
                        <td className="p-2.5 text-foreground/80">{ext.dureeAns} an{ext.dureeAns > 1 ? "s" : ""}</td>
                        <td className="p-2.5 text-right font-semibold text-foreground">{ext.prixRevendeur != null ? formatPrix(parseFloat(ext.prixRevendeur)) : "Sur devis"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 p-4 border-t border-border bg-card space-y-3">
          {item.statutProduit === 'a_venir' ? (
            <div className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-700">
              À venir — disponible prochainement
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Quantité</span>
                <QtySelector qty={qty} onChange={setQty} />
              </div>
              <button
                onClick={() => {
                  addItem({
                    productId: item.id,
                    sku: item.sku ?? "",
                    nomFr: item.nomFr,
                    prixAffiche: item.prixAffiche,
                    statutLabel: item.statutLabel,
                    imageUrl: item.imageUrl,
                  }, qty);
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primary }}
                data-testid="button-detail-add-to-devis"
              >
                <ShoppingCart className="w-4 h-4" />
                Ajouter au devis
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RobotCard({ item, primary, onDetail }: {
  item: CatalogueItem;
  primary: string;
  onDetail: () => void;
}) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  return (
    <div
      className="bg-card rounded-xl border border-border overflow-hidden flex flex-col hover:shadow-md transition-shadow"
      data-testid={`card-catalogue-${item.id}`}
    >
      <div
        className="flex items-center justify-center cursor-pointer bg-white dark:bg-zinc-900 border-b border-border"
        style={{ height: 200 }}
        onClick={onDetail}
      >
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-contain" />
        ) : (
          <Package className="w-12 h-12 text-muted-foreground/30" />
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-3">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            {item.sku && <p className="text-[10px] text-muted-foreground font-mono">{item.sku}</p>}
            {item.statutProduit === 'a_venir' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 font-medium">À venir</span>
            )}
            {item.categorieRobot && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">{item.categorieRobot}</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-tight line-clamp-1">{item.nomFr}</h3>
          {item.descriptionFr && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.descriptionFr}</p>
          )}
          {item.features && item.features.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {item.features.slice(0, 2).map((f, i) => (
                <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1">
                  <span className="text-primary shrink-0">•</span>{f}
                </li>
              ))}
              {item.features.length > 2 && (
                <li className="text-[10px] text-muted-foreground italic">+{item.features.length - 2} autres…</li>
              )}
            </ul>
          )}
        </div>

        <div>
          {item.statutProduit === 'a_venir' ? (
            <p className="text-sm italic text-muted-foreground">Prix à venir</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-foreground">
                {item.prixAffiche !== null
                  ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(item.prixAffiche)
                  : <span className="text-base text-muted-foreground">Prix sur demande</span>
                }
              </p>
              <p className="text-[10px] text-muted-foreground">Tarif {item.statutLabel}</p>
              {item.financementMin !== null && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Zap className="w-3 h-3" />
                  ou dès {formatPrix(item.financementMin)}/mois
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-auto">
          <button
            onClick={onDetail}
            className="w-full flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium border border-border text-foreground hover:bg-secondary/60 transition-colors"
            data-testid={`button-detail-${item.id}`}
          >
            Voir le détail
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          {item.statutProduit !== 'a_venir' && (
            <div className="flex items-center gap-2">
              <QtySelector qty={qty} onChange={setQty} />
              <button
                onClick={() => addItem({
                  productId: item.id,
                  sku: item.sku ?? "",
                  nomFr: item.nomFr,
                  prixAffiche: item.prixAffiche,
                  statutLabel: item.statutLabel,
                  imageUrl: item.imageUrl,
                }, qty)}
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium text-white transition-opacity hover:opacity-90 whitespace-nowrap"
                style={{ backgroundColor: primary }}
                data-testid={`button-add-devis-${item.id}`}
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                Ajouter au devis
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Couleurs par catégorie robot ───────────────────────────────────────────
const CAT_COLORS: Record<string, string> = {
  Service:    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  Nettoyage:  "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  Industriel: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
};

type SortKeyC = "nom" | "categorie" | "prix";
type SortDir  = "asc" | "desc";

function SortThC({ label, sortK, currentKey, currentDir, onToggle, right }: {
  label: string; sortK: SortKeyC; currentKey: SortKeyC; currentDir: SortDir;
  onToggle: (k: SortKeyC) => void; right?: boolean;
}) {
  const active = currentKey === sortK;
  return (
    <th
      className={`px-3 py-2.5 text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none whitespace-nowrap ${right ? "text-right" : "text-left"}`}
      onClick={() => onToggle(sortK)}
    >
      <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>
        {label}
        {active
          ? (currentDir === "asc" ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />)
          : <ChevronDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );
}

function CatalogueLightbox({ url, label, onClose }: { url: string; label: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/30 rounded-full p-2 transition-colors">
        <X className="w-6 h-6" />
      </button>
      <div className="max-w-[90vw] max-h-[80vh] flex flex-col items-center gap-4" onClick={e => e.stopPropagation()}>
        <img src={url} alt={label} className="max-w-full max-h-[70vh] object-contain drop-shadow-2xl rounded-lg" />
        <p className="text-white font-medium text-base text-center px-4">{label}</p>
      </div>
    </div>
  );
}

function RobotRow({ item, primary, onDetail, onLightbox }: {
  item: CatalogueItem; primary: string;
  onDetail: () => void;
  onLightbox: (url: string, label: string) => void;
}) {
  const { addItem } = useCart();
  const catColor = item.categorieRobot ? (CAT_COLORS[item.categorieRobot] ?? "bg-muted text-muted-foreground") : null;
  const canAdd = item.statutProduit !== "a_venir";

  return (
    <tr
      className="hover:bg-secondary/30 transition-colors cursor-pointer"
      onClick={onDetail}
      data-testid={`row-robot-${item.id}`}
    >
      {/* Photo */}
      <td className="px-4 py-2.5">
        <div
          className="w-12 h-12 rounded-lg bg-white dark:bg-zinc-900 border border-border overflow-hidden flex items-center justify-center flex-shrink-0"
          onClick={e => { if (item.imageUrl) { e.stopPropagation(); onLightbox(item.imageUrl, item.nomFr); } }}
        >
          {item.imageUrl
            ? <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-contain" />
            : <Package className="w-5 h-5 text-muted-foreground/30" />}
        </div>
      </td>
      {/* Nom */}
      <td className="px-3 py-2.5 max-w-[200px]">
        <p className="text-sm font-medium text-foreground truncate">{item.nomFr}</p>
      </td>
      {/* SKU */}
      <td className="px-3 py-2.5">
        <p className="text-xs font-mono text-muted-foreground">{item.sku ?? <span className="italic">À définir</span>}</p>
      </td>
      {/* Catégorie */}
      <td className="px-3 py-2.5">
        {catColor && item.categorieRobot
          ? <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${catColor}`}>{item.categorieRobot}</span>
          : <span className="text-muted-foreground">—</span>}
      </td>
      {/* Statut */}
      <td className="px-3 py-2.5">
        {item.statutProduit === "a_venir"
          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 font-medium whitespace-nowrap">À venir</span>
          : null}
      </td>
      {/* Prix d'achat */}
      <td className="px-3 py-2.5 text-right">
        {item.statutProduit === "a_venir"
          ? <span className="text-xs italic text-muted-foreground">À venir</span>
          : <span className={`text-sm font-semibold ${item.prixAffiche !== null ? "text-foreground" : "text-muted-foreground text-xs italic"}`}>
              {item.prixAffiche !== null
                ? <>{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(item.prixAffiche)}<span className="text-xs font-normal text-muted-foreground ml-0.5">HT</span></>
                : "Sur demande"}
            </span>}
      </td>
      {/* Location 36 mois */}
      <td className="px-3 py-2.5 text-right">
        {item.loc36Mois
          ? <span className="text-sm font-medium text-foreground">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(item.loc36Mois)}<span className="text-xs font-normal text-muted-foreground">/m</span></span>
          : <span className="text-muted-foreground">—</span>}
      </td>
      {/* Actions */}
      <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <button
            onClick={onDetail}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-foreground hover:bg-secondary/60 transition-colors whitespace-nowrap"
            data-testid={`button-detail-row-${item.id}`}
          >
            Voir la fiche
          </button>
          <button
            onClick={() => canAdd && addItem({ productId: item.id, sku: item.sku ?? "", nomFr: item.nomFr, prixAffiche: item.prixAffiche, statutLabel: item.statutLabel, imageUrl: item.imageUrl }, 1)}
            disabled={!canAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ backgroundColor: primary }}
            data-testid={`button-add-row-${item.id}`}
          >
            <ShoppingCart className="w-3 h-3" />
            Ajouter
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CataloguePage() {
  const { items, statutLabel, isLoading, error } = useCatalogue();
  const { brand } = usePortailAuth();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState<CatalogueItem | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; label: string } | null>(null);

  // Vue liste / grille avec persistence localStorage
  const [viewModePref, setViewModePref] = useState<"list" | "grid">(() => {
    const saved = localStorage.getItem("portail_catalogue_viewMode");
    return saved === "list" || saved === "grid" ? saved : "grid";
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const viewMode = isMobile ? "grid" : viewModePref;
  const setViewMode = (v: "list" | "grid") => {
    setViewModePref(v);
    localStorage.setItem("portail_catalogue_viewMode", v);
  };

  // Tri
  const [sortKey, setSortKey] = useState<SortKeyC>("nom");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const toggleSort = (k: SortKeyC) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter(p =>
      !q || p.nomFr.toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const sortedFiltered = useMemo(() => {
    if (viewMode !== "list") return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      if (sortKey === "nom")      { va = a.nomFr; vb = b.nomFr; }
      else if (sortKey === "categorie") { va = a.categorieRobot ?? ""; vb = b.categorieRobot ?? ""; }
      else { va = a.prixAffiche ?? -1; vb = b.prixAffiche ?? -1; }
      if (typeof va === "string") {
        const cmp = va.localeCompare(vb as string, "fr");
        return sortDir === "asc" ? cmp : -cmp;
      }
      const cmp = (va as number) - (vb as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, viewMode, sortKey, sortDir]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-4 border-b border-border/60 bg-card">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Catalogue robots</h1>
            <p className="text-sm text-muted-foreground">Découvrez notre gamme de robots de service</p>
          </div>
          {/* Toggle vue liste / grille */}
          {!isMobile && (
            <div className="flex items-center gap-1 border border-border rounded-lg p-0.5 flex-shrink-0">
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-md transition-colors ${viewModePref === "list" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="toggle-catalogue-list"
                title="Vue liste"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-md transition-colors ${viewModePref === "grid" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="toggle-catalogue-grid"
                title="Vue cartes"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou SKU…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="input-catalogue-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {statutLabel && (
            <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground border border-border">
              Tarif {statutLabel}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-24 text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun robot trouvé</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="px-4 py-2.5 w-16"></th>
                  <SortThC label="Nom" sortK="nom" currentKey={sortKey} currentDir={sortDir} onToggle={toggleSort} />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">SKU</th>
                  <SortThC label="Catégorie" sortK="categorie" currentKey={sortKey} currentDir={sortDir} onToggle={toggleSort} />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Statut</th>
                  <SortThC label="Tarif d'achat" sortK="prix" currentKey={sortKey} currentDir={sortDir} onToggle={toggleSort} right />
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">Location 36 m</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {sortedFiltered.map(item => (
                  <RobotRow
                    key={item.id}
                    item={item}
                    primary={primary}
                    onDetail={() => setSelectedItem(item)}
                    onLightbox={(url, label) => setLightbox({ url, label })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map(item => (
              <RobotCard
                key={item.id}
                item={item}
                primary={primary}
                onDetail={() => setSelectedItem(item)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedItem && (
        <DetailPanel
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          statutLabel={statutLabel}
          primary={primary}
        />
      )}

      {lightbox && (
        <CatalogueLightbox
          url={lightbox.url}
          label={lightbox.label}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
